export interface CarouselImage {
  src: string;
  heading: string;
  content: string;
}

export interface StatItem {
  value: string;
  displayValue: string;
  label: string;
  icon: string;
}

export interface PsCard {
  icon: string;
  title: string;
  desc: string;
  bgColor: string;
  glowColor: string;
}

export interface FeatureItem {
  icon: string;
  iconClass: string;
  title: string;
  desc: string;
}

export interface WhyItem {
  icon: string;
  title: string;
  desc: string;
}

export interface ConsolidatedFeature {
  icon: string;
  cls: string;
  title: string;
  desc: string;
}

export interface ComparisonRow {
  capability: string;
  traditional: string;
  gemfin: string;
  icon: string;
  iconBg: string;
}

export interface WorkflowStep {
  step: number;
  title: string;
  desc: string;
}

export interface SecurityItem {
  icon: string;
  title: string;
  desc: string;
}

export const CAROUSEL_IMAGES: CarouselImage[] = [
  {
    src: '../../../assets/images/onboard-client.png',
    heading: 'Onboard Clients Effortlessly',
    content:
      'Streamline client onboarding with automated workflows, KYC verification, and seamless account setup in minutes.'
  },
  {
    src: '../../../assets/images/listofclients.png',
    heading: 'Manage Clients Effectively',
    content:
      'Access comprehensive client profiles, track interactions, and maintain strong relationships with powerful management tools.'
  },
  {
    src: '../../../assets/images/holdings-slide.png',
    heading: 'Track Holdings in Real-Time',
    content:
      'Monitor portfolio performance, asset allocation, and investment holdings with detailed analytics and live updates.'
  },
  {
    src: '../../../assets/images/reports-dashboard.png',
    heading: 'Generate Insightful Reports',
    content:
      'Create customizable reports and dashboards to analyze performance, identify trends, and make data-driven decisions.'
  }
];

export const STATS: StatItem[] = [
  {
    value: '99.9%',
    displayValue: '99.9%',
    label: 'Platform Uptime',
    icon: 'pi pi-check-circle'
  }
];

export const PS_CARDS: PsCard[] = [
  {
    icon: '../../../assets/images/gemfin_landing01.png',
    title: 'Streamline Processes',
    desc: 'Say goodbye to manual bottlenecks. Simplify portfolio management with intuitive workflows, task automation, and real-time updates.',
    bgColor: 'rgba(24, 70, 125, 0.06)',
    glowColor: 'linear-gradient(90deg, #18467d, #2a6cb8)'
  },
  {
    icon: '../../../assets/images/gemfin_landing02.png',
    title: 'Comprehensive Insights',
    desc: 'Generate detailed, customisable reports that provide actionable insights for fund performance, risk management, and market trends.',
    bgColor: 'rgba(86, 197, 150, 0.08)',
    glowColor: 'linear-gradient(90deg, #56c596, #3aa87a)'
  },
  {
    icon: '../../../assets/images/gemfin_landing03.png',
    title: 'Stronger Client Relationships',
    desc: 'Deliver a superior experience with robust client management tools. Keep your clients informed, engaged, and satisfied.',
    bgColor: 'rgba(239, 68, 68, 0.06)',
    glowColor: 'linear-gradient(90deg, #ef4444, #dc2626)'
  }
];

export const FEATURES: FeatureItem[] = [
  {
    icon: 'pi pi-user-plus',
    iconClass: 'fi-blue',
    title: 'Onboarding Automation',
    desc: 'Digitize client onboarding with automated KYC, e-sign, and account setup — reduce turnaround from days to minutes.'
  },
  {
    icon: 'pi pi-chart-line',
    iconClass: 'fi-green',
    title: 'Portfolio Tracking',
    desc: 'Real-time portfolio monitoring with asset allocation, performance analytics, and gain/loss tracking across schemes.'
  },
  {
    icon: 'pi pi-users',
    iconClass: 'fi-orange',
    title: 'Client Management',
    desc: 'Centralized client profiles with communication history, document management, and relationship tracking.'
  },
  {
    icon: 'pi pi-file-check',
    iconClass: 'fi-purple',
    title: 'Reporting & Analytics',
    desc: 'Generate detailed portfolio statements, capital gains reports, and customizable dashboards effortlessly.'
  },
  {
    icon: 'pi pi-shield',
    iconClass: 'fi-teal',
    title: 'Compliance & KYC',
    desc: 'Built-in KYC verification, regulatory compliance checks, and audit trails — stay compliant with ease.'
  },
  {
    icon: 'pi pi-th-large',
    iconClass: 'fi-indigo',
    title: 'Dashboard Visualization',
    desc: 'Intuitive dashboards with key metrics, portfolio summaries, and actionable insights at a glance.'
  }
];

export const WHY_ITEMS: WhyItem[] = [
  {
    icon: 'pi pi-bolt',
    title: 'Faster Onboarding',
    desc: 'Reduce client onboarding time by up to 80% with automated digital workflows.'
  },
  {
    icon: 'pi pi-users',
    title: 'Better Client Management',
    desc: 'Comprehensive CRM built for wealth managers with 360° client views.'
  },
  {
    icon: 'pi pi-file-export',
    title: 'Automated Reporting',
    desc: 'One-click reports: portfolio statements, capital gains, and compliance documents.'
  },
  {
    icon: 'pi pi-server',
    title: 'Scalable Infrastructure',
    desc: 'Cloud-native platform that grows with your business — from 100 to 100,000 clients.'
  },
  {
    icon: 'pi pi-lock',
    title: 'Secure Platform',
    desc: 'Enterprise-grade security with encryption, secure access controls, and data protection.'
  },
  {
    icon: 'pi pi-briefcase',
    title: 'Built for Wealth Managers',
    desc: 'Purpose-designed for brokers, distributors, and RIAs with industry-specific workflows.'
  }
];

export const CONSOLIDATED_FEATURES: ConsolidatedFeature[] = [
  {
    icon: 'pi pi-box',
    cls: 'fl-blue',
    title: 'Plug-and-Play Setup',
    desc: 'No set up delays, start managing your client portfolios in 1 Day.'
  },
  {
    icon: 'pi pi-stopwatch',
    cls: 'fl-green',
    title: '2-Minute Onboarding',
    desc: 'Rapid distributor sign-up to get you started instantly.'
  },
  {
    icon: 'pi pi-users',
    cls: 'fl-orange',
    title: 'White-Glove Support',
    desc: 'You sign up; our team handles the full MFD account configuration.'
  },
  {
    icon: 'pi pi-wallet',
    cls: 'fl-purple',
    title: 'Zero Fixed Costs',
    desc: 'No setup fee or upfront fixed up, pay just 0.01% of AUM.'
  },
  {
    icon: 'pi pi-check-circle',
    cls: 'fl-teal',
    title: '99.9% Uptime',
    desc: 'Enterprise-grade reliability ensuring your platform is always available when you need it.'
  },
  {
    icon: 'pi pi-th-large',
    cls: 'fl-indigo',
    title: 'All-in-One Platform',
    desc: 'Onboard clients, track portfolios, generate reports, and grow your business — all from one powerful platform built for modern wealth management.'
  }
];

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    capability: 'Client Onboarding',
    traditional: 'Paper-based, days to complete',
    gemfin: 'Digital, minutes to complete',
    icon: 'pi pi-user-plus',
    iconBg: 'rgba(24, 70, 125, 0.08)'
  },
  {
    capability: 'Portfolio Tracking',
    traditional: 'Spreadsheets, manual updates',
    gemfin: 'Real-time, automated sync',
    icon: 'pi pi-chart-line',
    iconBg: 'rgba(86, 197, 150, 0.1)'
  },
  {
    capability: 'Report Generation',
    traditional: 'Hours of manual work',
    gemfin: 'One-click automated reports',
    icon: 'pi pi-file-export',
    iconBg: 'rgba(245, 158, 11, 0.1)'
  },
  {
    capability: 'Client Communication',
    traditional: 'Scattered emails & calls',
    gemfin: 'Centralized CRM with history',
    icon: 'pi pi-comments',
    iconBg: 'rgba(139, 92, 246, 0.1)'
  },
  {
    capability: 'Compliance',
    traditional: 'Manual checks, error-prone',
    gemfin: 'Automated, audit-ready',
    icon: 'pi pi-shield',
    iconBg: 'rgba(16, 185, 129, 0.1)'
  }
];

export const WORKFLOW_STEPS: WorkflowStep[] = [
  { step: 1, title: 'Onboard Clients', desc: 'Digital KYC & account setup' },
  { step: 2, title: 'Manage Portfolios', desc: 'Invest & allocate assets' },
  { step: 3, title: 'Track Performance', desc: 'Real-time analytics' },
  { step: 4, title: 'Generate Reports', desc: 'Automated statements' },
  { step: 5, title: 'Grow Business', desc: 'Scale with confidence' }
];

export const SECURITY_ITEMS: SecurityItem[] = [
  {
    icon: 'pi pi-lock',
    title: 'Data Encryption',
    desc: '256-bit AES encryption for all data at rest and in transit.'
  },
  {
    icon: 'pi pi-cloud',
    title: 'Secure Cloud',
    desc: 'Hosted on enterprise-grade cloud infrastructure with 99.9% uptime.'
  },
  {
    icon: 'pi pi-verified',
    title: 'Regulatory Compliance',
    desc: 'Compliant with SEBI, AMFI, and industry regulatory requirements.'
  },
  {
    icon: 'pi pi-shield',
    title: 'Client Data Protection',
    desc: 'Role-based access controls, audit logs, and secure data handling.'
  }
];
